import React, {useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {ChevronDown, HelpCircle} from 'lucide-react';

const faqs = [
    {
        question: 'Ile kosztuje montaż kamer w Mielcu i okolicach?',
        answer:
            'Podstawowy zestaw 2 do 4 kamer IP Full HD / 4K dla domu jednorodzinnego lub firmy w Mielcu i promieniu do 250 km to koszt od ok. 3 500 - 4 400 zł z kompletnym montażem, rejestratorem, dyskiem i konfiguracją aplikacji na telefonie. Każda wycena jest bezpłatna i dopasowana do Twojego obiektu.',
    },
    {
        question: 'Czy mogę oglądać obraz z kamer na telefonie, gdy jestem poza domem?',
        answer:
            'Tak! Konfigurujemy bezpłatną, bezpieczną aplikację na smartfony (iOS i Android) oraz komputery. Gdziekolwiek jesteś - w pracy, na urlopie za granicą czy w trasie - masz podgląd na żywo w jakości HD/4K, powiadomienia o wykryciu ruchu oraz dostęp do archiwalnych nagrań.',
    },
    {
        question: 'Jak szybko zamontujecie monitoring w Mielcu i okolicach?',
        answer:
            'Działamy błyskawicznie bez wielotygodniowych kolejek. Typowy montaż dla domu lub małej firmy (2 do 6 kamer) wykonujemy zazwyczaj w 1 dzień roboczy. Większe instalacje przemysłowe i magazynowe realizujemy w 2 do 4 dni.',
    },
    {
        question: 'Gdzie dokładnie działacie? Jaki jest Wasz zasięg dojazdu?',
        answer:
            'Nasza baza to Mielec (ul. Wojska Polskiego 9/D211, kod 39-300), natomiast działamy w promieniu do 250 km wokół Mielca! Regularnie montujemy systemy w takich miastach jak: Rzeszów, Tarnobrzeg, Dębica, Tarnów, Stalowa Wola, Kolbuszowa, Połaniec, Staszów, Sandomierz, Krosno, Jasło, Nowy Sącz, Kraków, Kielce, Lublin i okolicznych powiatach.',
    },
    {
        question: 'Czy oferujecie blokadę reklam na domową sieć Wi-Fi (anty-reklama)?',
        answer:
            'Tak! Wdrażamy dedykowane mini PC (mikrokomputery DNS), które blokują natrętne reklamy, banery i trackery śledzące na KAŻDYM urządzeniu połączonym z Twoim domowym Wi-Fi: na telewizorach Smart TV (m.in. YouTube), telefonach iPhone/Android, tabletach, laptopach i konsolach. Zero wtyczek do instalacji, bezpieczny internet dla dzieci i wyższa prędkość sieci.',
    },
    {
        question: 'Jaką gwarancję otrzymuję na kamery i montaż?',
        answer:
            'Na wszystkie urządzenia dajemy pełną gwarancję producenta (do 3 lat), a na wykonany montaż i okablowanie gwarancję jakości. Zapewniamy także lokalne wsparcie serwisowe i serwis pogwarancyjny w Mielcu i do 250 km.',
    },
    {
        question: 'Czy montujecie kamery zarówno w domach prywatnych, jak i w firmach?',
        answer:
            'Tak, obsługujemy klientów indywidualnych (domy jednorodzinne, bliźniaki, domki letniskowe, działki) oraz biznesowych (hale produkcyjne, magazyny, sklepy, biura, parkingi, gospodarstwa rolne). Do każdego projektu podchodzimy indywidualnie.',
    },
    {
        question: 'Czy oprócz kamer montujecie też alarmy, domofony i sieci LAN/WiFi?',
        answer:
            'Tak, oferujemy kompleksowe instalacje teletechniczne: bezprzewodowe i przewodowe systemy alarmowe z powiadomieniem GSM, wideodomofony IP z podglądem na telefonie, kontrolę dostępu oraz profesjonalne sieci komputerowe LAN i WiFi MESH.',
    },
];

function FAQItem({
                     faq,
                     isOpen,
                     onToggle,
                 }: {
    faq: { question: string; answer: string };
    isOpen: boolean;
    onToggle: () => void;
}) {
    return (
        <div
            className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                isOpen
                    ? 'border-primary/40 bg-card shadow-lg shadow-primary/5'
                    : 'border-border/60 bg-card/60 hover:border-border hover:bg-card'
            }`}
        >
            <button
                type="button"
                onClick={onToggle}
                className="flex items-center justify-between w-full p-5 sm:p-6 text-left cursor-pointer gap-4 select-none"
                aria-expanded={isOpen}
            >
                <span className="font-semibold text-base sm:text-lg text-foreground leading-snug">
                    {faq.question}
                </span>
                <motion.div
                    animate={{rotate: isOpen ? 180 : 0}}
                    transition={{duration: 0.3, ease: 'easeInOut'}}
                    className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        isOpen ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                    }`}
                >
                    <ChevronDown size={16}/>
                </motion.div>
            </button>

            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial={{height: 0, opacity: 0}}
                        animate={{height: 'auto', opacity: 1}}
                        exit={{height: 0, opacity: 0}}
                        transition={{duration: 0.3, ease: 'easeInOut'}}
                    >
                        <div className="px-5 pb-5 sm:px-6 sm:pb-6 pt-0">
                            <div className="h-px w-full bg-border/60 mb-4"/>
                            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                                {faq.answer}
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export function FAQSection() {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    return (
        <section id="faq" className="w-full scroll-mt-20 py-20 md:py-28 px-5 bg-background">
            <div className="max-w-4xl mx-auto">
                <motion.div
                    initial={{opacity: 0, y: 30}}
                    whileInView={{opacity: 1, y: 0}}
                    viewport={{once: true}}
                    transition={{duration: 0.6}}
                    className="text-center mb-12"
                >
                    <div
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border bg-primary/5 text-xs md:text-sm font-semibold text-primary mb-4">
                        <HelpCircle size={14}/>
                        Najczęstsze pytania o monitoring w Mielcu i promieniu 250 km
                    </div>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                        Masz pytania? Mamy{' '}
                        <span className="gradient-text">odpowiedzi</span>
                    </h2>
                    <p className="text-muted-foreground max-w-xl mx-auto text-base">
                        Wszystko, co warto wiedzieć o montażu kamer, monitoringu CCTV, blokadzie reklam Wi-Fi i instalacjach w Mielcu i promieniu do 250 km.
                    </p>
                </motion.div>

                <div className="space-y-3">
                    {faqs.map((faq, i) => (
                        <motion.div
                            key={i}
                            initial={{opacity: 0, y: 15}}
                            whileInView={{opacity: 1, y: 0}}
                            viewport={{once: true}}
                            transition={{duration: 0.4, delay: i * 0.05}}
                        >
                            <FAQItem
                                faq={faq}
                                isOpen={openIndex === i}
                                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                            />
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
